import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/client.js';
import type { JobRole } from '../../api/endpoints.js';
import { orgApi } from '../../api/endpoints.js';
import { queryKeys } from '../../app/query-client.js';
import { HeaderCell, TableHeaderRow, TableShell } from '../../components/data-table.js';
import { HelpHint } from '../../components/help-hint.js';
import { PageHeader } from '../../components/page-header.js';
import { Select } from '../../components/select.js';
import { Alert, Badge, Button, COMPACT_CONTROL as FIELD, Spinner } from '../../components/ui.js';
import { useDepartments, useJobRoles } from '../../hooks/use-directory.js';

const ANY = 'any';
const NO_DEPARTMENT = 'none';

const SELECT_TRIGGER = `${FIELD} flex items-center gap-2 text-left font-semibold`;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

const POSITIONS_HINT = (
  <>
    <p>
      What someone may raise follows their{' '}
      <strong className="font-semibold text-navy dark:text-dark-ink">position</strong>, not their
      name. Everyone holding a position inherits its capabilities.
    </p>
    <p>
      That is on purpose: "who may start a purchase" is a question about the job, not about the
      person doing it this month. Change the position once and everyone in it follows.
    </p>
    <p>
      The count in each row is how many active accounts hold it — the reach of any box you tick.
    </p>
  </>
);

const DEPARTMENT_HINT = (
  <>
    <p>
      Which department this job sits in. It is what lets the People screen ask for a department
      first and then offer only the positions inside it.
    </p>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">Grouping, not
      permission.</strong> What somebody may reach is still decided by their own memberships, so
      moving a position here changes nobody's access.
    </p>
    <p>
      A position with no department is not offered when choosing one for a person. Placing it
      fixes that.
    </p>
  </>
);

const STATUS_HINT = (
  <>
    <p>
      A retired position stops being offered when adding or editing a person. Nobody already
      holding it is touched.
    </p>
    <p>
      It cannot be retired while active accounts hold it — move them elsewhere first. The count
      in the row is exactly that number.
    </p>
  </>
);

export function JobRolesPage(): ReactNode {
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('true');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ANY);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDepartmentId, setNewDepartmentId] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const { departments } = useDepartments();
  const { jobRoles: allRoles, isLoading } = useJobRoles(true);

  const fail = (fallback: string) => (error: unknown) => {
    setSaved(null);
    setProblem(error instanceof ApiError ? error.message : fallback);
  };

  const done = async (id: string | null): Promise<void> => {
    setProblem(null);
    setSaved(id);
    setEditing(null);
    await queryClient.invalidateQueries({ queryKey: queryKeys.org.jobRoles.all });
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
  };

  const update = useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof orgApi.updateJobRole>[1]) =>
      orgApi.updateJobRole(id, input),
    onSuccess: (role) => done(role.id),
    onError: fail('That position could not be changed.'),
  });

  const create = useMutation({
    mutationFn: () =>
      orgApi.createJobRole({
        slug: slugify(newName),
        name: newName.trim(),
        departmentId: newDepartmentId,
      }),
    onSuccess: async (role) => {
      setNewName('');
      setNewDepartmentId('');
      setAdding(false);
      await done(role.id);
    },
    onError: fail('That position could not be created.'),
  });

  const needle = search.trim().toLowerCase();

  const roles = allRoles.filter((role) => {
    if (statusFilter !== ANY && role.isActive !== (statusFilter === 'true')) return false;
    if (departmentFilter === NO_DEPARTMENT && role.departmentId !== null) return false;
    if (
      departmentFilter !== ANY &&
      departmentFilter !== NO_DEPARTMENT &&
      role.departmentId !== departmentFilter
    ) {
      return false;
    }
    return needle === '' || role.name.toLowerCase().includes(needle);
  });

  const unplaced = allRoles.filter(
    (role) => role.isActive && role.departmentId === null,
  ).length;

  const newSlug = slugify(newName);
  const mayCreate = newName.trim().length >= 2 && newSlug.length >= 2 && newDepartmentId !== '';
  const noDepartments = departments.length === 0;

  return (
    <>
      <PageHeader title="Positions" subtitle="Job titles, and what each one is allowed to raise.">
        <HelpHint title="Positions">{POSITIONS_HINT}</HelpHint>
        <Button
          type="button"
          size="sm"
          disabled={noDepartments}
          {...(noDepartments ? { title: 'Create a department first — a position has to sit in one.' } : {})}
          onClick={() => {
            setAdding(true);
            setProblem(null);
          }}
        >
          Add position
        </Button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-7 sm:py-6">
        {problem === null ? null : <Alert tone="error">{problem}</Alert>}

        {noDepartments ? (
          <Alert tone="info">
            There are no departments yet, and every position sits in one. Create a department
            first and this screen becomes usable.
          </Alert>
        ) : null}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="Search positions"
            className="h-11 min-w-[220px] flex-1 rounded-field border border-[rgb(223,228,235)] bg-white px-3.5 text-sm font-normal text-[rgb(20,32,51)] outline-none focus:border-royal/55 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
          />

          <Select
            label="Department filter"
            value={departmentFilter}
            onChange={setDepartmentFilter}
            options={[
              { value: ANY, label: 'Any department' },
              { value: NO_DEPARTMENT, label: 'Not placed yet' },
              ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
            ]}
          />

          <Select
            label="Status filter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'true', label: 'In use' },
              { value: 'false', label: 'Retired' },
              { value: ANY, label: 'All positions' },
            ]}
          />
        </div>

        {unplaced === 0 ? null : (
          <p className="mb-4 rounded-field border border-caution/25 bg-caution-soft px-3.5 py-2.5 text-[13px] text-caution">
            {unplaced === 1 ? 'One position has' : `${String(unplaced)} positions have`} no
            department yet, so {unplaced === 1 ? 'it is' : 'they are'} not offered when choosing a
            position for somebody. Use the department filter to find{' '}
            {unplaced === 1 ? 'it' : 'them'}.
          </p>
        )}

        {isLoading ? (
          <div className="grid place-items-center py-20 text-muted">
            <Spinner className="size-5" />
          </div>
        ) : (
          <TableShell>
            <TableHeaderRow>
              <HeaderCell className="min-w-[120px] max-w-[28rem] flex-1">Position</HeaderCell>
              <HeaderCell className="hidden w-40 shrink-0 items-center gap-1.5 lg:flex">
                Department
                <HelpHint title="Department">{DEPARTMENT_HINT}</HelpHint>
              </HeaderCell>
              <HeaderCell className="hidden w-20 shrink-0 sm:block">Held by</HeaderCell>
              <HeaderCell className="flex w-[168px] shrink-0 items-center justify-end gap-1.5">
                Status
                <HelpHint title="Status">{STATUS_HINT}</HelpHint>
              </HeaderCell>
            </TableHeaderRow>

            {adding ? (
              <div className="flex w-full flex-wrap items-center gap-3 border-b border-hairline bg-royal/4 px-4 py-3 dark:border-dark-line dark:bg-white/5">
                <input
                  autoFocus
                  value={newName}
                  placeholder="e.g. Finance Associate"
                  onChange={(event) => {
                    setNewName(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && mayCreate) create.mutate();
                    if (event.key === 'Escape') setAdding(false);
                  }}
                  className={`${FIELD} min-w-[200px] flex-1`}
                />

                <div className="w-44 shrink-0">
                  <Select
                    label="Department"
                    value={newDepartmentId}
                    onChange={setNewDepartmentId}
                    className={SELECT_TRIGGER}
                    options={[
                      { value: '', label: 'Choose a department…' },
                      ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
                    ]}
                  />
                </div>

                <span className="hidden text-[12.5px] text-muted xl:block">
                  {newSlug === '' ? 'No capabilities to start' : `Identifier: ${newSlug}`}
                </span>

                <Button
                  type="button"
                  size="sm"
                  loading={create.isPending}
                  disabled={!mayCreate}
                  onClick={() => {
                    create.mutate();
                  }}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setAdding(false);
                    setNewName('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : null}

            {roles.map((role, index) =>
              editing === role.id ? (
                <EditRoleRow
                  key={role.id}
                  role={role}
                  departments={departments}
                  busy={update.isPending}
                  onCancel={() => {
                    setEditing(null);
                  }}
                  onSave={(input) => {
                    update.mutate({ id: role.id, ...input });
                  }}
                />
              ) : (
                <RoleRow
                  key={role.id}
                  role={role}
                  first={index === 0 && !adding}
                  justSaved={saved === role.id}
                  busy={update.isPending}
                  onEdit={() => {
                    setEditing(role.id);
                    setProblem(null);
                  }}
                  onChange={(input) => {
                    update.mutate({ id: role.id, ...input });
                  }}
                />
              ),
            )}

            {roles.length === 0 ? (
              <p className="px-4 py-10 text-center text-[13.5px] text-muted">
                Nothing matches that.
              </p>
            ) : null}
          </TableShell>
        )}
      </div>
    </>
  );
}

function RoleRow({
  role,
  first,
  justSaved,
  busy,
  onEdit,
  onChange,
}: {
  readonly role: JobRole;
  readonly first: boolean;
  readonly justSaved: boolean;
  readonly busy: boolean;
  readonly onEdit: () => void;
  readonly onChange: (input: { isActive?: boolean }) => void;
}): ReactNode {
  const held = role.userCount;

  return (
    <div
      className={`flex w-full items-center gap-4 px-4 py-2.5 ${
        first ? '' : 'border-t border-hairline dark:border-dark-line'
      } ${role.isActive ? '' : 'bg-mist/40 dark:bg-white/3'}`}
    >
      <span className="min-w-[120px] max-w-[28rem] flex-1">
        <span className="block truncate text-[14px] font-semibold text-navy dark:text-dark-ink">
          {role.name}
          {role.isActive ? null : (
            <>
              {' '}
              <Badge tone="neutral">Retired</Badge>
            </>
          )}
          {justSaved ? (
            <>
              {' '}
              <Badge tone="positive">Saved</Badge>
            </>
          ) : null}
        </span>
        <span className="block truncate text-[12px] text-muted lg:hidden">
          {role.departmentName ?? 'No department yet'}
          {held === 0 ? '' : ` · held by ${String(held)}`}
        </span>
      </span>

      <span className="hidden w-40 shrink-0 truncate text-[12.5px] lg:block">
        {role.departmentName === null ? (
          <span className="text-caution">Not placed</span>
        ) : (
          <span className="text-muted">{role.departmentName}</span>
        )}
      </span>

      <span className="hidden w-20 shrink-0 text-[12.5px] text-muted sm:block">
        {held === 0 ? '—' : held}
      </span>

      <span className="flex w-[168px] shrink-0 justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy || (role.isActive && held > 0)}
          title={
            role.isActive && held > 0
              ? `Move ${held === 1 ? 'the one person' : `all ${String(held)} people`} to another position first.`
              : undefined
          }
          onClick={() => {
            onChange({ isActive: !role.isActive });
          }}
        >
          {role.isActive ? 'Retire' : 'Restore'}
        </Button>
      </span>
    </div>
  );
}

function EditRoleRow({
  role,
  departments,
  busy,
  onCancel,
  onSave,
}: {
  readonly role: JobRole;
  readonly departments: readonly { readonly id: string; readonly name: string }[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (input: { name: string; departmentId: string | null }) => void;
}): ReactNode {
  const [name, setName] = useState(role.name);
  const [departmentId, setDepartmentId] = useState(role.departmentId ?? '');

  const ready = name.trim().length >= 2;

  const save = (): void => {
    if (!ready) return;
    onSave({ name: name.trim(), departmentId: departmentId === '' ? null : departmentId });
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-3 border-t border-hairline bg-royal/4 px-4 py-3 dark:border-dark-line dark:bg-white/5">
      <input
        autoFocus
        value={name}
        aria-label="Position name"
        onChange={(event) => {
          setName(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') onCancel();
        }}
        className={`${FIELD} min-w-[180px] flex-1`}
      />

      <div className="w-44 shrink-0">
        <Select
          label="Department"
          value={departmentId}
          onChange={setDepartmentId}
          className={SELECT_TRIGGER}
          options={[
            { value: '', label: 'No department yet' },
            ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
        />
      </div>

      <span className="hidden text-[12.5px] text-muted xl:block">{role.slug}</span>

      <Button type="button" size="sm" loading={busy} disabled={!ready} onClick={save}>
        Save
      </Button>
      <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
